import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Users, Wallet, BadgeCheck, CalendarRange, CalendarDays } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import Reveal from "./Reveal";
import { LogoTitle } from "./Common";
import { cityContent } from "../../seo";
import { aCity, slugify, estimate } from "../../mock";
import { CITY_LOCAL, NATIONAL_RULE, RULES_UPDATED } from "../../cityLocal";
import { pricePath, quartiersOf, quartierPath } from "../../seoPages";

const Paras = ({ text }) => text.split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>);

export function CityGuide({ city, ai }) {
  const c = cityContent(city.name);
  return (
    <section className="pb-20" data-testid="city-guide">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-4">
          <LogoTitle as="h2" size="md" title="louer" script={`${aCity(city.name)}, le guide`} />
          {c.near.length > 0 && (
            <ul className="mt-8 space-y-2" data-testid="city-distances">
              {c.near.map((n) => (
                <li key={n.name}>
                  <Link to={`/villes/${slugify(n.name)}`} className="flex items-center justify-between rounded-2xl bg-white ring-1 ring-[#e6dcc8] px-5 py-3 font-semibold text-[#13261c] hover:bg-[#13261c] hover:text-white transition-colors">
                    <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#c4703f]" />{n.name}</span><span className="text-sm text-[#c4703f]">{n.km} km</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Reveal>
        <Reveal delay={100} className="lg:col-span-8 space-y-5 text-[17px] text-[#4a5249] leading-relaxed">
          {ai ? (
            <div data-testid="city-ai-content" className="space-y-5">
              <p className="text-lg text-[#2a4636] font-medium" data-testid="city-ai-intro">{ai.intro}</p>
              {ai.sections.map((s, i) => (
                <div key={i} className="space-y-4 pt-3" data-testid={`city-ai-section-${i}`}>
                  <h2 className="font-logo font-black tracking-[-0.02em] text-2xl lg:text-[28px] text-[#13261c] leading-tight">{s.title}</h2>
                  <Paras text={s.text} />
                </div>
              ))}
              <p className="pt-3">{c.paragraphs[2]}</p>
            </div>
          ) : c.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
        </Reveal>
      </div>
    </section>
  );
}

export function CityFaq({ city, ai, base }) {
  const faq = [...(ai?.faq || []), ...(base || cityContent(city.name).faq)];
  return (
    <section className="py-24 bg-[#faf7f0]" data-testid="city-faq">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4">
          <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#c4703f]">FAQ {city.name}</span>
          <div className="mt-4"><LogoTitle as="h2" size="md" title="vos questions" script={aCity(city.name)} /></div>
          <Link to="/faq" data-testid="city-faq-all" className="mt-6 inline-flex items-center gap-2 font-bold text-[#c4703f] hover:text-[#13261c] transition-colors">Voir toute la FAQ <ArrowRight className="w-4 h-4" /></Link>
        </Reveal>
        <Reveal className="lg:col-span-8" delay={100}>
          <Accordion type="single" collapsible defaultValue="q-0" className="space-y-3">
            {faq.map((f, i) => (
              <AccordionItem key={i} value={`q-${i}`} className="border border-[#e6dcc8] border-l-4 border-l-transparent rounded-2xl px-6 bg-[#f6f1e7] data-[state=open]:bg-white data-[state=open]:border-l-[#d9895a] transition-[background-color,border-color]">
                <AccordionTrigger data-testid={`city-faq-${i}`} className="text-left text-[17px] font-bold text-[#13261c] hover:no-underline py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[15px] text-[#5a6158] leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}

const eur = (v, k = 10) => `${(Math.round(v / k) * k).toLocaleString("fr-FR")} €`;
const WHY = (at) => [
  [Users, "Une équipe sur place", `Notre équipe locale intervient sur place ${at} 7j/7 pour l'accueil, le ménage et les imprévus.`],
  [CalendarRange, "Loué toute l'année", "Prix ajustés chaque nuit selon la saison, les week-ends et les événements, diffusion sur Airbnb, Booking et Vrbo."],
  [Wallet, "Vos revenus, directement", "Les revenus vous sont versés 24h après chaque arrivée, avec un relevé clair chaque mois."],
  [BadgeCheck, "Sans frais d'entrée", "Ni abonnement ni frais de mise en place : une commission uniquement sur les revenus perçus."],
];

export function CityWhy({ city }) {
  const at = aCity(city.name);
  const { months, annual } = estimate({ city: city.name, type: "appartement", bedrooms: 1, guests: 2 });
  const figs = [["En janvier", months[0].value], ["En octobre", months[9].value], ["En août", months[7].value], ["Sur l'année", annual]];
  return (
    <section className="pb-20" data-testid="city-why">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal><LogoTitle as="h2" size="md" title="pourquoi nous confier" script={`votre logement ${at}`} /></Reveal>
        <div className="mt-10 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {WHY(at).map(([Icon, t, d], i) => (
              <Reveal key={t} delay={i * 70} className="rounded-3xl bg-white ring-1 ring-[#e6dcc8] p-6 hover:ring-[#d9895a] transition-shadow" data-testid={`city-why-${i}`}>
                <span className="w-11 h-11 rounded-2xl bg-[#f6e3d5] grid place-items-center"><Icon className="w-5 h-5 text-[#c4703f]" /></span>
                <p className="mt-4 font-bold text-lg text-[#13261c]">{t}</p>
                <p className="mt-1.5 text-[15px] text-[#5a6158] leading-relaxed">{d}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={120} className="lg:col-span-5 rounded-[32px] bg-[#13261c] text-[#faf7f0] p-8 grain relative overflow-hidden flex flex-col" data-testid="city-year-figures">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#d9895a]">Un T2 {at}, toute l'année</p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {figs.map(([l, v], i) => (
                <div key={l} className={`rounded-2xl p-4 ${i === 3 ? "bg-[#d9895a] text-[#13261c]" : "bg-white/10"}`} data-testid={`city-fig-${i}`}>
                  <p className={`text-xs font-semibold ${i === 3 ? "" : "text-[#faf7f0]/70"}`}>{l}</p>
                  <p className="mt-1 text-xl sm:text-2xl font-extrabold whitespace-nowrap">≈ {eur(v, i === 3 ? 100 : 10)}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-[#faf7f0]/60 leading-relaxed" data-testid="city-fig-disclaimer">Revenus locatifs bruts indicatifs pour 1 chambre et 2 voyageurs, issus de notre simulateur. Ils varient selon le logement, la saison et les réservations et ne constituent pas une garantie.</p>
            <div className="mt-auto pt-6 flex flex-wrap gap-3">
              <Link to="/rendez-vous" data-testid="city-why-rdv" className="inline-flex items-center gap-2 rounded-full bg-[#d9895a] hover:bg-[#e39d72] text-[#13261c] px-5 py-3 font-bold transition-colors"><CalendarDays className="w-4 h-4" />Prendre rendez-vous</Link>
              <Link to={`/proprietaires?city=${encodeURIComponent(city.name)}`} data-testid="city-why-sim" className="inline-flex items-center gap-2 rounded-full ring-1 ring-[#faf7f0]/40 hover:bg-white/10 px-5 py-3 font-bold transition-colors">Simuler mon logement<ArrowRight className="w-4 h-4" /></Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function CityLocal({ city }) {
  const l = CITY_LOCAL[city.name];
  if (!l) return null;
  const at = aCity(city.name);
  return (
    <section className="pb-20" data-testid="city-local">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal><LogoTitle as="h2" size="md" title="quartier par quartier" script={`louer ${at}`} /></Reveal>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {l.quartiers.map(([n, t], i) => (
            <Reveal key={n} delay={i * 60} className="rounded-3xl bg-white ring-1 ring-[#e6dcc8] p-6">
              <h3 className="font-bold text-[#13261c] flex items-center gap-2" data-testid={`city-quartier-${i}`}><MapPin className="w-4 h-4 text-[#c4703f] shrink-0" />{n}</h3>
              <p className="mt-2 text-[15px] text-[#4a5249] leading-relaxed">{t}</p>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 grid lg:grid-cols-2 gap-10 text-[17px] text-[#4a5249] leading-relaxed">
          <Reveal><h3 className="font-logo font-black text-2xl text-[#13261c]">Les logements qui se louent le mieux {at}</h3><p className="mt-3">{l.logements}</p></Reveal>
          <Reveal delay={80}><h3 className="font-logo font-black text-2xl text-[#13261c]">{at.replace(/^à/, "À").replace(/^au/, "Au")}, la saison dure toute l'année</h3><p className="mt-3">{l.saisons}</p></Reveal>
        </div>
        <Reveal className="mt-10">
          <div className="rounded-[32px] bg-[#13261c] text-[#faf7f0] p-8 lg:p-10" data-testid="city-rules">
            <h3 className="font-logo font-black text-2xl">Règles de location {at}</h3>
            <ul className="mt-5 space-y-3">
              {l.regles.map((r) => <li key={r} className="flex gap-3"><BadgeCheck className="w-5 h-5 text-[#d9895a] shrink-0 mt-0.5" />{r}</li>)}
            </ul>
            <p className="mt-5 text-sm text-[#cfd6cf]">{NATIONAL_RULE}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link to={pricePath(city.name)} className="rounded-full bg-[#d9895a] text-[#13261c] px-5 py-2.5 text-sm font-bold hover:bg-[#e8a27a] transition-colors" data-testid="city-price-link">Prix de la conciergerie {at}</Link>
              {quartiersOf(city.name).map((q) => <Link key={q.slug} to={quartierPath(q)} className="rounded-full ring-1 ring-white/30 px-5 py-2.5 text-sm font-bold hover:bg-white/10 transition-colors" data-testid={`city-quartier-link-${q.slug}`}>Conciergerie {q.name}</Link>)}
            </div>
            <p className="mt-4 text-sm text-[#cfd6cf]">Mis à jour : {RULES_UPDATED} · Source : <a href={l.source[1]} target="_blank" rel="noopener noreferrer" className="underline hover:text-white" data-testid="city-rules-source">{l.source[0]}</a>. Ces informations peuvent évoluer : nous vérifions avec vous la situation de votre logement avant la première réservation.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

